import React from 'react';
import type { VideoProject, VideoAudioSettings as AudioSettingsType } from './videoStudioTypes';

interface VideoAudioSettingsProps {
  project: VideoProject;
  onChangeProject: (patch: Partial<VideoProject>) => void;
  onChangeAudio: (audio: AudioSettingsType) => void;
}

export const VideoAudioSettings: React.FC<VideoAudioSettingsProps> = ({
  project,
  onChangeProject,
  onChangeAudio,
}) => {
  const audio = project.audio_settings || {
    narration_enabled: true,
    voice: 'pt-BR-natural',
    language: 'pt-BR',
    music_style: 'inspiradora e moderna',
    sound_fx: 'suaves'
  };

  const handleAudioChange = (patch: Partial<AudioSettingsType>) => {
    onChangeAudio({ ...audio, ...patch });
  };

  return (
    <div>
      <div style={{ marginBottom: '1.25rem' }}>
        <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>Áudio, Narração & Roteiro Base</h4>
        <p style={{ margin: '0.2rem 0 0 0', fontSize: '0.825rem', color: 'var(--text-secondary, #64748b)' }}>
          Configure a trilha sonora, locução e ajuste as mensagens centrais do anúncio.
        </p>
      </div>

      <div className="vstudio-card" style={{ marginBottom: '1.5rem' }}>
        <h5 style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', fontWeight: 600 }}>Locução & Sonorização</h5>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
          <div>
            <label className="vstudio-form-label">Narração por Voz</label>
            <select
              className="vstudio-select"
              value={audio.narration_enabled ? 'enabled' : 'disabled'}
              onChange={(e) => handleAudioChange({ narration_enabled: e.target.value === 'enabled' })}
            >
              <option value="enabled">Ativada (com locução)</option>
              <option value="disabled">Desativada (apenas trilha)</option>
            </select>
          </div>

          <div>
            <label className="vstudio-form-label">Perfil da Voz</label>
            <select
              className="vstudio-select"
              value={audio.voice}
              disabled={!audio.narration_enabled}
              onChange={(e) => handleAudioChange({ voice: e.target.value })}
            >
              <option value="pt-BR-natural">pt-BR Natural & Acolhedora</option>
              <option value="pt-BR-comercial-masc">pt-BR Comercial (Masculina)</option>
              <option value="pt-BR-comercial-fem">pt-BR Comercial (Feminina)</option>
              <option value="pt-BR-jovem">pt-BR Jovem & Enérgica</option>
            </select>
          </div>

          <div>
            <label className="vstudio-form-label">Estilo da Trilha Musical</label>
            <input
              type="text"
              className="vstudio-input"
              value={audio.music_style || ''}
              onChange={(e) => handleAudioChange({ music_style: e.target.value })}
              placeholder="Ex: Comercial leve, moderna e cativante"
            />
          </div>

          <div>
            <label className="vstudio-form-label">Efeitos Sonoros (SFX)</label>
            <input
              type="text"
              className="vstudio-input"
              value={audio.sound_fx || ''}
              onChange={(e) => handleAudioChange({ sound_fx: e.target.value })}
              placeholder="Ex: Whoosh suave nas transições"
            />
          </div>
        </div>
      </div>

      <div className="vstudio-card" style={{ marginBottom: '1.5rem' }}>
        <h5 style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', fontWeight: 600 }}>Formato & Parâmetros de Vídeo</h5>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
          <div>
            <label className="vstudio-form-label">Proporção da Tela (Aspect Ratio)</label>
            <select
              className="vstudio-select"
              value={project.aspect_ratio || '9:16'}
              onChange={(e) => onChangeProject({ aspect_ratio: e.target.value as any })}
            >
              <option value="9:16">9:16 Vertical (Stories, Reels & TikTok)</option>
              <option value="16:9">16:9 Horizontal (YouTube & TV)</option>
              <option value="1:1">1:1 Quadrado (Feed Instagram / LinkedIn)</option>
            </select>
          </div>

          <div>
            <label className="vstudio-form-label">Duração Alvo do Vídeo</label>
            <select
              className="vstudio-select"
              value={String(project.target_duration_seconds || 15)}
              onChange={(e) => onChangeProject({ target_duration_seconds: Number(e.target.value) || 15 })}
            >
              <option value="10">10 segundos (Rápido • Alto Impacto)</option>
              <option value="15">15 segundos (Padrão Stories / Reels)</option>
              <option value="30">30 segundos (Comercial Completo)</option>
              <option value="45">45 segundos (Explicativo Detalhado)</option>
              <option value="60">60 segundos (Demonstração Completa)</option>
            </select>
          </div>
        </div>
      </div>

      <div className="vstudio-card">
        <h5 style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', fontWeight: 600 }}>Pilares do Roteiro (Copywriting)</h5>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '0.75rem' }}>
          <div>
            <label className="vstudio-form-label">Objetivo Central do Roteiro (Script Goal)</label>
            <input
              type="text"
              className="vstudio-input"
              value={project.script_goal || ''}
              onChange={(e) => onChangeProject({ script_goal: e.target.value })}
              placeholder="Ex: Conscientizar empresários sobre o Split Payment e gerar leads para o diagnóstico"
            />
          </div>

          <div>
            <label className="vstudio-form-label">Gancho Inicial (Hook - Primeiros 3 segundos)</label>
            <input
              type="text"
              className="vstudio-input"
              value={project.script_hook || ''}
              onChange={(e) => onChangeProject({ script_hook: e.target.value })}
              placeholder="Gancho de atenção"
            />
          </div>

          <div>
            <label className="vstudio-form-label">Corpo / Proposta de Valor (Body)</label>
            <textarea
              rows={2}
              className="vstudio-textarea"
              value={project.script_body || ''}
              onChange={(e) => onChangeProject({ script_body: e.target.value })}
              placeholder="Desenvolvimento do benefício"
            />
          </div>

          <div>
            <label className="vstudio-form-label">Chamada para Ação Final (CTA)</label>
            <input
              type="text"
              className="vstudio-input"
              value={project.script_cta || ''}
              onChange={(e) => onChangeProject({ script_cta: e.target.value })}
              placeholder="Ex: Peça pelo WhatsApp ou visite a loja hoje mesmo!"
            />
          </div>

          <div>
            <label className="vstudio-form-label">Diretrizes Gerais de Estilo & Ambientação</label>
            <textarea
              rows={2}
              className="vstudio-textarea"
              value={project.style_notes || ''}
              onChange={(e) => onChangeProject({ style_notes: e.target.value })}
              placeholder="Estilo visual, atmosfera, paleta de cores ou iluminação"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
